import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import theme from '../../../theme/theme';
import { SubNavBar } from '../../../components/ui/subNavBar/SubNavBar';
import { PAYMENT_SECTIONS, type PaymentSectionKey } from '../config/paymentSections';
import { PayslipsTable } from './PayslipsTable';
import { PayslipDetail } from './PayslipDetail';

const BORDER = `1px solid ${theme.palette.divider}`;
const TEXT_MUTED = theme.palette.text.label;

interface PaymentSectionsProps {
  /** Employee whose payment data (payslips, …) is displayed. */
  employeeId: string;
}

/**
 * Payment feature entry point — the Paiement tab of the employee detail page.
 * Hosts the sibling read-only consultation sections (Bulletins, Acomptes,
 * Emprunts) via the shared {@link SubNavBar}. Bulletins renders the payslips
 * table; Acomptes/Emprunts show a coming-soon state until their endpoints land.
 */
export function PaymentSections({ employeeId }: PaymentSectionsProps) {
  const { t } = useTranslation('payments');
  const [active, setActive] = useState<PaymentSectionKey>('payslips');
  // The payslips section swaps between the list and a selected payslip's detail.
  const [selectedPayslipId, setSelectedPayslipId] = useState<string | null>(null);
  const activeSection = PAYMENT_SECTIONS.find(section => section.key === active)!;

  // Switching sub-sections always returns to the list (never a stale detail).
  const changeSection = (next: PaymentSectionKey) => {
    setSelectedPayslipId(null);
    setActive(next);
  };

  return (
    <Box
      sx={{
        px: 3.5,
        py: 0.75 /* 6px top/bottom */,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── payment sub-navigation (shared SubNavBar) — fixed above the scroll ── */}
      <Box sx={{ borderBottom: BORDER, mb: 2, flexShrink: 0 }}>
        <SubNavBar<PaymentSectionKey>
          items={PAYMENT_SECTIONS.map(section => ({
            value: section.key,
            label: t(section.labelKey),
            icon: section.icon,
          }))}
          value={active}
          onChange={changeSection}
          ariaLabel={t('common:employeeDetail.tabs.paiement')}
        />
      </Box>

      {/* Fills the remaining height; the payslips table scrolls internally so the
          sub-nav above stays visible. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* ── Bulletins: payslips list, or the selected payslip's detail ────── */}
        {active === 'payslips' &&
          (selectedPayslipId ? (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <PayslipDetail
                employeeId={employeeId}
                payslipId={selectedPayslipId}
                onBack={() => setSelectedPayslipId(null)}
              />
            </Box>
          ) : (
            <PayslipsTable
              employeeId={employeeId}
              onOpenDetail={payslip => setSelectedPayslipId(payslip.hc_uuid_key)}
            />
          ))}

        {/* ── Acomptes / Emprunts: coming-soon empty state ─────────────────── */}
        {active !== 'payslips' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1,
              px: 2.5,
              py: 7,
            }}
          >
            <Box
              sx={{
                width: theme.avatar.lg,
                height: theme.avatar.lg,
                borderRadius: theme.borderRadius.md,
                bgcolor: theme.palette.grey[100],
                color: theme.palette.action.disabled,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5,
              }}
            >
              {activeSection.emptyIcon}
            </Box>
            <Typography
              sx={{
                fontSize: theme.typography.fontSizeTitle,
                fontWeight: theme.typography.fontWeightSemiBold,
                color: theme.palette.text.body,
              }}
            >
              {t(activeSection.labelKey)}
            </Typography>
            <Typography
              sx={{
                fontSize: theme.typography.fontSizeDescription,
                color: TEXT_MUTED,
                maxWidth: '42ch',
              }}
            >
              {t('comingSoon')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
